if (!customElements.get('sticky-add-to-cart')) {
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.scrollThreshold = 200;
        this.syncInterval = null;
        this.observer = null;
        this.buttonObserver = null;
        this.formButtonsObserver = null;
        this.galleryObserver = null;
        this.variantSelectorObserver = null;
        this.variantSelectorSyncObserver = null;
        this.mainVariantSyncTarget = null;
        this.mainVariantSyncHandler = null;
        this.mainQuantityInput = null;
        this.mainQuantitySyncHandler = null;
        this.formButtonsInViewport = true;
        this.galleryInViewport = true;
        this.variantSelectorInViewport = false;
        this.retryCount = 0;
        this.maxRetries = 20;
        this.isInitialized = false;
      }
  
      connectedCallback() {
        this.stickyButton = this.querySelector(`#StickyAddToCart-${this.sectionId}`);
        
        if (!this.stickyButton) return; 
  
        this.setupScrollHandler();
        this.setupFormButtonsVisibility();
        this.setupGalleryVisibility();
        this.setupVariantSelectorVisibility();
        this.setupMobileWrapperClick();
        this.setupMobileSelectCrossClick();
        this.setupMobileTabsClick();
        this.findMainButton();
      }
  
      disconnectedCallback() {
        if (this.syncInterval) {
          clearInterval(this.syncInterval);
        }
        if (this.observer) {
          this.observer.disconnect();
        }
        if (this.buttonObserver) {
          this.buttonObserver.disconnect();
        }
        if (this.formButtonsObserver) {
          this.formButtonsObserver.disconnect();
        }
        if (this.galleryObserver) {
          this.galleryObserver.disconnect();
        }
        if (this.variantSelectorObserver) {
          this.variantSelectorObserver.disconnect();
        }
        if (this.variantSelectorSyncObserver) {
          this.variantSelectorSyncObserver.disconnect();
        }
        if (this.mainVariantSyncTarget && this.mainVariantSyncHandler) {
          this.mainVariantSyncTarget.removeEventListener('change', this.mainVariantSyncHandler, true);
          this.mainVariantSyncTarget.removeEventListener('input', this.mainVariantSyncHandler, true);
        }
        if (this.mainQuantityInput && this.mainQuantitySyncHandler) {
          this.mainQuantityInput.removeEventListener('input', this.mainQuantitySyncHandler);
          this.mainQuantityInput.removeEventListener('change', this.mainQuantitySyncHandler);
        }
        if (this.handleScroll) {
          window.removeEventListener('scroll', this.handleScroll);
        }
        if (this.mobileWrapperClickHandler) {
          const mobileWrapper = this.querySelector('.select-product-mobile-wrapper');
          if (mobileWrapper) {
            mobileWrapper.removeEventListener('click', this.mobileWrapperClickHandler);
          }
        }
        if (this.mobileSelectCrossClickHandler) {
          const mobileSelectCross = this.querySelector('.mobile-select-cross span');
          if (mobileSelectCross) {
            mobileSelectCross.removeEventListener('click', this.mobileSelectCrossClickHandler);
          }
        }
        if (this.mobileTabsClickHandler) {
          const tabsContainer = this.querySelector('.sticky-mobile-tabs');
          if (tabsContainer) {
            tabsContainer.removeEventListener('click', this.mobileTabsClickHandler);
          }
        }
      }
  
      findMainButton() {
        this.mainButton = document.getElementById(`AddToCart-${this.sectionId}`);
        
        if (this.mainButton) {
          this.initialize();
        } else {
          // Button not found, set up observer to watch for it
          this.setupButtonObserver();
          // Also retry periodically as fallback
          this.retryFindButton();
        }
      }
  
      setupButtonObserver() {
        // Watch the product-info element or document body for the button to appear
        const productInfo = document.querySelector('product-info');
        const watchTarget = productInfo || document.body;
        
        this.buttonObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            // Check if the button was added
            if (mutation.type === 'childList') {
              for (const node of mutation.addedNodes) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  // Check if this node is the button or contains it
                  if (node.id === `AddToCart-${this.sectionId}` || 
                      node.querySelector && node.querySelector(`#AddToCart-${this.sectionId}`)) {
                    this.mainButton = document.getElementById(`AddToCart-${this.sectionId}`);
                    if (this.mainButton && !this.isInitialized) {
                      this.initialize();
                      this.buttonObserver.disconnect();
                      return;
                    }
                  }
                }
              }
            }
            
            // Also check attributes in case button is shown/hidden
            if (mutation.type === 'attributes' && mutation.target.id === `AddToCart-${this.sectionId}`) {
              if (!this.isInitialized) {
                this.mainButton = document.getElementById(`AddToCart-${this.sectionId}`);
                if (this.mainButton) {
                  this.initialize();
                  this.buttonObserver.disconnect();
                  return;
                }
              }
            }
          }
        });
  
        this.buttonObserver.observe(watchTarget, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['id', 'style', 'class']
        });
      }
  
      retryFindButton() {
        if (this.isInitialized) return;
        
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          setTimeout(() => {
            this.mainButton = document.getElementById(`AddToCart-${this.sectionId}`);
            if (this.mainButton) {
              this.initialize();
              if (this.buttonObserver) {
                this.buttonObserver.disconnect(); 
              }
            } else {
              this.retryFindButton();
            }
          }, 500);
        }
      }
  
      initialize() {
        if (this.isInitialized || !this.mainButton || !this.stickyButton) return;
        
        this.isInitialized = true;
        this.setupClickHandler();
        this.setupVariantAccordion();
        this.syncStickyAccordionFromCurrentVariantSelector();
        this.bootstrapStickyPromoPanels();
        this.setupMainVariantSync();
        this.setupQuantitySync();
        this.setupStateSync();
        this.syncButtonState();
      }

      bootstrapStickyPromoPanels() {
        const syncPanels = () => {
          this.syncStickyPromotionClonePanels();
          this.syncStickyFreeSockPromoPanel();
        };

        // Build promo panels on first paint and retry briefly while PDP widgets settle.
        syncPanels();
        [120, 300, 600, 1000].forEach((delay) => {
          setTimeout(syncPanels, delay);
        });
      }

      syncStickyAccordionFromCurrentVariantSelector() {
        const tryRebuild = () => this.rebuildStickyAccordionFromVariantSelector();

        // Try immediately and with a few retries because variant-selector can be rebuilt asynchronously.
        if (tryRebuild()) return;
        [120, 300, 600, 1000].forEach((delay) => {
          setTimeout(() => {
            tryRebuild();
          }, delay);
        });

        // Keep sticky options synced to any first-load variant-selector DOM mutations.
        if (this.variantSelectorSyncObserver) {
          this.variantSelectorSyncObserver.disconnect();
        }
        const variantSelector = this.getVariantSelectorElement();
        if (!variantSelector || typeof MutationObserver === 'undefined') return;

        this.variantSelectorSyncObserver = new MutationObserver(() => {
          tryRebuild();
        });

        this.variantSelectorSyncObserver.observe(variantSelector, {
          childList: true,
          subtree: true
        });

        // Stop observing after initial load window to avoid unnecessary long-lived observers.
        setTimeout(() => {
          if (this.variantSelectorSyncObserver) {
            this.variantSelectorSyncObserver.disconnect();
            this.variantSelectorSyncObserver = null;
          }
        }, 2500);
      }

      getMainQuantityInput() {
        return (
          document.getElementById(`Quantity-${this.sectionId}`) ||
          document.querySelector(`#product-form-${this.sectionId} input[name="quantity"]`)
        );
      }

      get isTiloPromotionTemplate() {
        const templateSuffix = String(this.dataset.productTemplateSuffix || '').trim().toLowerCase();
        if (templateSuffix) {
          return templateSuffix === 'tilo_promotion';
        }
        return document.body.classList.contains('product-template-suffix-tilo_promotion');
      }

      get isTiloPromotionYouthStyleActive() {
        const styleVariantsElement = document.querySelector('style-variants');
        if (!styleVariantsElement) return false;

        const activeStyleVariants = styleVariantsElement.querySelectorAll('.color-variant-single.active');
        if (activeStyleVariants.length === 1) {
          const activeStyleGroup = activeStyleVariants[0].closest('.style-variant-group');
          const activeStyleGroupValue = String(activeStyleGroup?.dataset?.styleGroup || '').trim().toLowerCase();
          if (!activeStyleGroupValue) return false;
          return activeStyleGroupValue === 'youth';
        }

        let currentProductHandle = '';
        const productUrl = String(this.dataset.productUrl || '').trim();
        if (productUrl) {
          try {
            const url = new URL(productUrl, window.location.origin);
            const pathSegments = url.pathname.split('/').filter(Boolean);
            const productsIndex = pathSegments.indexOf('products');
            if (productsIndex !== -1) {
              currentProductHandle = String(pathSegments[productsIndex + 1] || '').trim().toLowerCase();
            }
          } catch (error) {
            const match = productUrl.match(/\/products\/([^/?#]+)/i);
            currentProductHandle = match?.[1] ? String(match[1]).trim().toLowerCase() : '';
          }
        } else {
          const pathSegments = window.location.pathname.split('/').filter(Boolean);
          const productsIndex = pathSegments.indexOf('products');
          if (productsIndex !== -1) {
            currentProductHandle = String(pathSegments[productsIndex + 1] || '').trim().toLowerCase();
          }
        }

        let styleVariantForCurrentProduct = null;
        if (currentProductHandle) {
          styleVariantForCurrentProduct = styleVariantsElement.querySelector(
            `.color-variant-single[data-product-handle="${currentProductHandle}"]`
          );
        }

        const activeStyleVariant =
          styleVariantForCurrentProduct ||
          styleVariantsElement.querySelector('.color-variant-single.active');

        if (!activeStyleVariant) return false;

        const activeStyleGroup = activeStyleVariant.closest('.style-variant-group');
        const activeStyleGroupValue = String(activeStyleGroup?.dataset?.styleGroup || '').trim().toLowerCase();
        if (!activeStyleGroupValue) return false;
        return activeStyleGroupValue === 'youth';
      }

      setupQuantitySync() {
        const stickyQuantitySelector = this.querySelector('.sticky-product-quantity quantity-selector');
        const stickyQuantityInput = this.querySelector('[data-sticky-quantity-input]');
        if (!stickyQuantityInput || !stickyQuantitySelector) return;

        const clampToStep = (value, minValue, maxValue, stepValue) => {
          const normalizedStep = stepValue > 0 ? stepValue : 1;
          const normalizedMin = Number.isFinite(minValue) ? minValue : 1;
          let nextValue = Number.isFinite(value) ? value : normalizedMin;
          if (Number.isFinite(maxValue)) {
            nextValue = Math.min(maxValue, nextValue);
          }
          nextValue = Math.max(normalizedMin, nextValue);
          return nextValue;
        };

        const updateStickyQuantity = (requestedValue) => {
          const minValue = parseInt(stickyQuantityInput.getAttribute('min'), 10);
          const maxRawValue = parseInt(stickyQuantityInput.getAttribute('max'), 10);
          const stepRawValue = parseInt(stickyQuantityInput.getAttribute('step'), 10);
          const min = Number.isNaN(minValue) ? 1 : minValue;
          const max = Number.isNaN(maxRawValue) ? Infinity : maxRawValue;
          const step = Number.isNaN(stepRawValue) ? 1 : stepRawValue;

          const normalized = clampToStep(parseInt(requestedValue, 10), min, max, step);
          stickyQuantityInput.value = String(normalized);
          this.syncQuantityToMainForm(String(normalized));
        };

        const syncHandler = () => {
          updateStickyQuantity(stickyQuantityInput.value);
        };

        stickyQuantityInput.addEventListener('change', syncHandler);
        stickyQuantityInput.addEventListener('input', syncHandler);

        stickyQuantitySelector.addEventListener('click', (event) => {
          const triggerButton = event.target.closest('button[name="plus"], button[name="minus"]');
          if (!triggerButton) return;
          event.preventDefault();

          const currentValue = parseInt(stickyQuantityInput.value, 10);
          const stepRawValue = parseInt(stickyQuantityInput.getAttribute('step'), 10);
          const step = Number.isNaN(stepRawValue) ? 1 : stepRawValue;
          const baseValue = Number.isNaN(currentValue) ? parseInt(stickyQuantityInput.getAttribute('min'), 10) || 1 : currentValue;
          const nextValue = triggerButton.name === 'minus' ? baseValue - step : baseValue + step;
          updateStickyQuantity(nextValue);
        });

        const mainQuantityInput = this.getMainQuantityInput();
        if (mainQuantityInput) {
          this.mainQuantityInput = mainQuantityInput;
          this.mainQuantitySyncHandler = () => {
            if (document.activeElement === stickyQuantityInput) return;
            updateStickyQuantity(mainQuantityInput.value);
          };
          this.mainQuantityInput.addEventListener('input', this.mainQuantitySyncHandler);
          this.mainQuantityInput.addEventListener('change', this.mainQuantitySyncHandler);
        }
      }

      getVariantSelectorElement() {
        return document.querySelector('#variant-selector-' + this.sectionId);
      }

      setupMainVariantSync() {
        const variantSelector = this.getVariantSelectorElement();
        if (!variantSelector) return;

        if (this.mainVariantSyncTarget && this.mainVariantSyncHandler) {
          this.mainVariantSyncTarget.removeEventListener('change', this.mainVariantSyncHandler, true);
          this.mainVariantSyncTarget.removeEventListener('input', this.mainVariantSyncHandler, true);
        }

        this.mainVariantSyncTarget = variantSelector;
        this.mainVariantSyncHandler = () => {
          // Keep sticky selection and CTA state aligned with main selector changes.
          this.syncStickySelectionFromMainVariant();
          this.syncStickyPromotionClonePanels();
          this.syncStickyFreeSockPromoPanel();
          this.syncButtonState();
        };

        variantSelector.addEventListener('change', this.mainVariantSyncHandler, true);
        variantSelector.addEventListener('input', this.mainVariantSyncHandler, true);
      }

      syncStickySelectionFromMainVariant() {
        const accordion = this.querySelector('[data-sticky-variant-accordion]');
        const variantSelector = this.getVariantSelectorElement();
        if (!accordion || !variantSelector) return;

        const pickerType = variantSelector.dataset.pickerType || 'button';
        const optionItems = accordion.querySelectorAll('.sticky-variant-accordion__item');

        optionItems.forEach((item) => {
          const optionPosition = item.dataset.optionPosition;
          const optionIndex = parseInt(item.dataset.optionIndex, 10);
          const optionName = String(
            item.querySelector('.sticky-variant-accordion__trigger')?.dataset?.optionName || ''
          ).trim();

          let selectedValue = '';

          if (pickerType === 'dropdown' && Number.isFinite(optionIndex)) {
            const select = variantSelector.querySelector('#Option-' + this.sectionId + '-' + optionIndex);
            selectedValue = String(select?.value || '');
          } else {
            const radioName = optionName + '-' + (optionPosition || '');
            const selectedInput = variantSelector.querySelector(
              'input[type="radio"][name="' + radioName + '"]:checked'
            );
            selectedValue = String(selectedInput?.value || '');
          }

          if (!selectedValue) return;

          const triggerValue = item.querySelector('[data-sticky-current-value]');
          if (triggerValue) {
            triggerValue.textContent = this.getDisplayValueForOption(optionName, selectedValue);
          }

          item.querySelectorAll('.sticky-variant-accordion__option').forEach((optionButton) => {
            optionButton.classList.toggle('is-selected', optionButton.dataset.value === selectedValue);
          });
        });
      }

      syncQuantityToMainForm(quantityValue) {
        const mainQuantityInput = this.getMainQuantityInput();
        if (!mainQuantityInput || quantityValue == null || quantityValue === '') return;

        if (mainQuantityInput.value !== String(quantityValue)) {
          mainQuantityInput.value = quantityValue;
          mainQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.syncTiloPromotionMultiSizeFromMain();
      }

      syncTiloPromotionMultiSizeFromMain() {
        const productInfoEl = window.Shop?.productInfoEl || document.querySelector('product-info');
        if (!productInfoEl) return;

        if (typeof productInfoEl.setupTiloPromotionMultiSize === 'function') {
          productInfoEl.setupTiloPromotionMultiSize();
        }
        if (typeof productInfoEl.syncTiloPromotionSizeSelectors === 'function') {
          productInfoEl.syncTiloPromotionSizeSelectors();
        }

        this.syncStickyPromotionClonePanels();
        this.syncStickyFreeSockPromoPanel();
        setTimeout(() => {
          this.syncStickyPromotionClonePanels();
          this.syncStickyFreeSockPromoPanel();
        }, 120);
      }

      isAdultSelectionActive() {
        const variantSelector = document.querySelector('#variant-selector-' + this.sectionId);
        if (!variantSelector) return false;

        const selectedValues = [];
        variantSelector.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
          selectedValues.push(input.value || '');
        });
        variantSelector.querySelectorAll('select').forEach((select) => {
          selectedValues.push(select.value || '');
        });

        return selectedValues.some((value) => String(value).toLowerCase().includes('adult'));
      }

      updateStickyQuantityVisibility() {
        const stickyQtyWrapper = this.querySelector('.sticky-product-quantity');
        const stickyQuantityInput = this.querySelector('[data-sticky-quantity-input]');
        if (!stickyQtyWrapper || !stickyQuantityInput) return;

        const productInfoEl = window.Shop?.productInfoEl || document.querySelector('product-info');
        let hideQuantity = this.isAdultSelectionActive();
        if (this.isTiloPromotionTemplate) {
          if (productInfoEl && typeof productInfoEl.isTiloPromotionMultiSizeActive === 'function') {
            hideQuantity = !productInfoEl.isTiloPromotionMultiSizeActive();
          } else {
            hideQuantity = !this.isTiloPromotionYouthStyleActive;
          }
        }
        stickyQtyWrapper.style.display = hideQuantity ? 'none' : '';
        stickyQtyWrapper.setAttribute('aria-hidden', hideQuantity ? 'true' : 'false');

        if (hideQuantity) {
          stickyQuantityInput.value = '1';
          if (this.isAdultSelectionActive()) {
            this.syncQuantityToMainForm('1');
          }
        }
      }

      getStickyOptionNameFromSelect(selectElement, fallbackIndex) {
        const selectName = String(selectElement?.name || '');
        const nameMatch = selectName.match(/^options\[(.*)\]$/);
        if (nameMatch && nameMatch[1]) {
          return nameMatch[1];
        }

        const selectId = selectElement?.id;
        if (selectId) {
          const associatedLabel = document.querySelector(`label[for="${selectId}"]`);
          const associatedLabelText = String(associatedLabel?.textContent || '').trim();
          if (associatedLabelText) return associatedLabelText;
        }

        return 'Option ' + String(fallbackIndex + 1);
      }

      getDisplayValueForOption(optionName, rawValue) {
        const optionNameLower = String(optionName || '').trim().toLowerCase();
        const valueText = String(rawValue || '').trim();
        if (!optionNameLower.includes('size')) return valueText;

        switch (valueText.toLowerCase()) {
          case 'extra small':
          case 'xsmall':
          case 'x-small':
            return 'XS';
          case 'small':
            return 'S';
          case 'medium':
            return 'M';
          case 'large':
            return 'L';
          case 'extra large':
            return 'XL';
          default:
            return valueText;
        }
      }

      getMainPromotionCloneContainer() {
        const productInfoEl = window.Shop?.productInfoEl || document.querySelector('product-info');
        if (!productInfoEl) return null;
        return productInfoEl.querySelector('[data-tilo-promotion-size-groups]');
      }

      getMainFreeSockPromoElement() {
        const productInfoEl = window.Shop?.productInfoEl || document.querySelector('product-info');
        if (!productInfoEl) return null;
        const promo = productInfoEl.querySelector('tilo-free-sock-promo');
        if (!promo) return null;
        if (promo.dataset.promoActive !== 'true') return null;
        if (promo.hidden || promo.style.display === 'none') return null;
        return promo;
      }

      getStickySizePanel() {
        const accordion = this.querySelector('[data-sticky-variant-accordion]');
        if (!accordion) return null;

        const sizeItem = Array.from(accordion.querySelectorAll('.sticky-variant-accordion__item')).find((item) => {
          const trigger = item.querySelector('.sticky-variant-accordion__trigger');
          const optionName = String(trigger?.dataset?.optionName || '').trim().toLowerCase();
          return optionName.includes('size');
        });
        const targetItem = sizeItem || accordion.querySelector('.sticky-variant-accordion__item');
        if (!targetItem) return null;
        return targetItem.querySelector('.sticky-variant-accordion__panel');
      }

      syncPromotionCloneSelectionToMain(cloneIndex, cloneValue) {
        const mainCloneContainer = this.getMainPromotionCloneContainer();
        if (!mainCloneContainer || !cloneIndex || !cloneValue) return;

        const sourceGroup = mainCloneContainer.querySelector(`[data-tilo-clone-index="${cloneIndex}"]`);
        if (!sourceGroup) return;

        const sourceSelect = sourceGroup.querySelector('select');
        if (sourceSelect) {
          sourceSelect.value = cloneValue;
          sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
          this.syncTiloPromotionMultiSizeFromMain();
          return;
        }

        const sourceRadio = Array.from(sourceGroup.querySelectorAll('input[type="radio"]')).find(
          (input) => String(input.value || '') === String(cloneValue)
        );
        if (sourceRadio) {
          sourceRadio.checked = true;
          sourceRadio.dispatchEvent(new Event('change', { bubbles: true }));
          this.syncTiloPromotionMultiSizeFromMain();
        }
      }

      syncFreeSockSelectionToMain(sockVariantId) {
        const mainPromo = this.getMainFreeSockPromoElement();
        if (!mainPromo || !sockVariantId) return;
        const mainButton = mainPromo.querySelector(`.tilo-free-sock-promo__size[data-sock-variant-id="${sockVariantId}"]`);
        if (!mainButton || mainButton.hasAttribute('disabled')) return;
        mainButton.click();
      }

      normalizeStickyCloneControls(clonedGroup, cloneIndex) {
        const idMap = new Map();
        const controls = clonedGroup.querySelectorAll('input, select');
        controls.forEach((control, controlIndex) => {
          const tagName = control.tagName.toLowerCase();
          const originalId = control.id ? String(control.id) : '';
          const nextId = `sticky-tilo-${cloneIndex}-${tagName}-${controlIndex}`;
          if (originalId) idMap.set(originalId, nextId);
          control.id = nextId;
          control.setAttribute('data-sticky-clone-control', 'true');
          control.setAttribute('data-clone-index', String(cloneIndex));
          control.removeAttribute('form');

          if (control instanceof HTMLInputElement && control.type === 'radio') {
            control.name = `${control.name || 'option'}-sticky-${cloneIndex}`;
          }
        });

        const labels = clonedGroup.querySelectorAll('label[for]');
        labels.forEach((label) => {
          const originalFor = String(label.getAttribute('for') || '');
          const mappedFor = idMap.get(originalFor);
          if (mappedFor) {
            label.setAttribute('for', mappedFor);
          } else {
            label.removeAttribute('for');
          }
        });
      }

      syncStickyPromotionClonePanels() {
        if (!this.isTiloPromotionTemplate) return;

        const sizePanel = this.getStickySizePanel();
        if (!sizePanel) return;

        const existing = sizePanel.querySelector('.sticky-tilo-promotion-size-groups');
        if (existing) existing.remove();

        const mainCloneContainer = this.getMainPromotionCloneContainer();
        if (!mainCloneContainer || mainCloneContainer.hidden || mainCloneContainer.style.display === 'none') return;

        const sourceGroups = mainCloneContainer.querySelectorAll('[data-tilo-clone-index]');
        if (!sourceGroups.length) return;

        const stickyGroupsWrap = document.createElement('div');
        stickyGroupsWrap.className = 'sticky-tilo-promotion-size-groups';

        sourceGroups.forEach((sourceGroup) => {
          const cloneIndex = sourceGroup.getAttribute('data-tilo-clone-index');
          if (!cloneIndex) return;
          const clonedGroup = sourceGroup.cloneNode(true);
          this.normalizeStickyCloneControls(clonedGroup, cloneIndex);
          stickyGroupsWrap.appendChild(clonedGroup);
        });

        if (!stickyGroupsWrap.children.length) return;
        sizePanel.appendChild(stickyGroupsWrap);
      }

      syncStickyFreeSockPromoPanel() {
        const sizePanel = this.getStickySizePanel();
        if (!sizePanel) return;

        const existingPromo = sizePanel.querySelector('.sticky-tilo-free-sock-promo');
        if (existingPromo) existingPromo.remove();

        const hasStyleVariants = !!document.querySelector('style-variants');
        const isAdultStyle =
          this.isAdultSelectionActive() || (hasStyleVariants && !this.isTiloPromotionYouthStyleActive);
        if (!isAdultStyle) return;

        const mainPromo = this.getMainFreeSockPromoElement();
        if (!mainPromo) return;

        const promoInner = mainPromo.querySelector('.tilo-free-sock-promo__inner');
        if (!promoInner) return;

        const stickyPromoWrap = document.createElement('div');
        stickyPromoWrap.className = 'sticky-tilo-free-sock-promo';
        stickyPromoWrap.appendChild(promoInner.cloneNode(true));
        sizePanel.appendChild(stickyPromoWrap);
      }

      renderStickyAccordionOption(accordion, optionData) {
        const sectionId = this.sectionId;
        const chevronSrc = this.dataset.stickyChevronSrc || '';
        const optionDisplayName = optionData.displayName || optionData.name;
        const optionSyncName = optionData.syncName || optionData.name;

        const label = document.createElement('p');
        label.className = 'sticky-variant-accordion__label';
        label.textContent = optionDisplayName;

        const item = document.createElement('div');
        item.className = 'sticky-variant-accordion__item';
        item.dataset.optionPosition = String(optionData.position);
        item.dataset.optionIndex = String(optionData.index);

        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'sticky-variant-accordion__trigger';
        trigger.setAttribute('aria-expanded', 'false');
        trigger.id = 'sticky-variant-trigger-' + sectionId + '-' + optionData.position;
        trigger.setAttribute('aria-controls', 'sticky-variant-panel-' + sectionId + '-' + optionData.position);
        trigger.dataset.optionName = optionSyncName;

        const spanVal = document.createElement('span');
        spanVal.className = 'sticky-variant-accordion__value';
        spanVal.setAttribute('data-sticky-current-value', '');
        spanVal.textContent = this.getDisplayValueForOption(optionDisplayName, optionData.currentValue || '');

        const spanIcon = document.createElement('span');
        spanIcon.className = 'sticky-variant-accordion__icon';
        spanIcon.setAttribute('aria-hidden', 'true');

        const chevron = document.createElement('img');
        chevron.src = chevronSrc;
        chevron.width = 16;
        chevron.height = 16;
        chevron.alt = '';
        chevron.className = 'sticky-variant-accordion__icon';
        spanIcon.appendChild(chevron);

        trigger.appendChild(spanVal);
        trigger.appendChild(spanIcon);

        const panel = document.createElement('div');
        panel.className = 'sticky-variant-accordion__panel';
        panel.id = 'sticky-variant-panel-' + sectionId + '-' + optionData.position;
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', trigger.id);
        panel.setAttribute('hidden', '');

        const optsWrap = document.createElement('div');
        optsWrap.className = 'sticky-variant-accordion__options';

        optionData.values.forEach((valueData) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'sticky-variant-accordion__option' + (valueData.value === optionData.currentValue ? ' is-selected' : '');
          btn.dataset.optionName = optionSyncName;
          btn.dataset.optionPosition = String(optionData.position);
          btn.dataset.optionIndex = String(optionData.index);
          btn.dataset.value = valueData.value;
          if (!valueData.available) btn.disabled = true;
          const displayValue = this.getDisplayValueForOption(optionDisplayName, valueData.value);
          btn.textContent = displayValue + (!valueData.available ? ' (Sold out)' : '');
          optsWrap.appendChild(btn);
        });

        panel.appendChild(optsWrap);
        item.appendChild(trigger);
        item.appendChild(panel);

        accordion.appendChild(label);
        accordion.appendChild(item);
      }

      rebuildStickyAccordionFromVariantSelector() {
        const accordion = this.querySelector('[data-sticky-variant-accordion]');
        const variantSelector = this.getVariantSelectorElement();
        if (!accordion || !variantSelector) return false;

        const pickerType = variantSelector.dataset.pickerType || 'button';
        const nextOptions = [];

        if (pickerType === 'dropdown') {
          const selects = variantSelector.querySelectorAll('select');
          selects.forEach((select, selectIndex) => {
            const optionName = this.getStickyOptionNameFromSelect(select, selectIndex);
            if (optionName.toLowerCase() === 'color') return;

            const rawId = String(select.id || '');
            const idMatch = rawId.match(/Option-[^-]+-(\d+)$/);
            const optionIndex = idMatch ? Number(idMatch[1]) : selectIndex;
            const optionPosition = optionIndex + 1;

            const optionValues = Array.from(select.options).map((optionEl) => {
              const optionValue = String(optionEl.value || '').trim();
              const optionLabelText = String(optionEl.textContent || '');
              const looksUnavailable =
                optionEl.disabled ||
                /unavailable|sold out/i.test(optionLabelText) ||
                String(optionEl.style.textDecoration || '').includes('line-through');
              return {
                value: optionValue,
                available: !looksUnavailable,
              };
            }).filter((entry) => entry.value !== '');

            if (!optionValues.length) return;

            nextOptions.push({
              name: optionName,
              position: optionPosition,
              index: optionIndex,
              currentValue: String(select.value || optionValues[0].value || ''),
              values: optionValues,
            });
          });
        } else {
          const fieldsets = variantSelector.querySelectorAll('.variant-select__button .product-form__input');
          fieldsets.forEach((fieldset, fieldsetIndex) => {
            const legend = fieldset.querySelector('legend');
            const optionDisplayName = String(legend?.textContent || '').trim() || ('Option ' + String(fieldsetIndex + 1));

            const radios = fieldset.querySelectorAll('input[type="radio"]');
            if (!radios.length) return;

            const valuesMap = new Map();
            radios.forEach((radio) => {
              const rawValue = String(radio.value || '').trim();
              if (!rawValue || valuesMap.has(rawValue)) return;
              const isAvailable = !radio.disabled && !radio.classList.contains('disabled');
              valuesMap.set(rawValue, { value: rawValue, available: isAvailable });
            });

            const optionValues = Array.from(valuesMap.values());
            if (!optionValues.length) return;

            const firstRadio = radios[0];
            const nameMatch = String(firstRadio.name || '').match(/-(\d+)$/);
            const optionPosition = nameMatch ? Number(nameMatch[1]) : fieldsetIndex + 1;
            const optionIndex = Math.max(0, optionPosition - 1);
            const syncNameMatch = String(firstRadio.name || '').match(/^(.*)-\d+$/);
            const optionSyncName = (syncNameMatch && syncNameMatch[1]) ? syncNameMatch[1] : optionDisplayName;
            if (String(optionSyncName).trim().toLowerCase() === 'color') return;

            const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
            const currentValue = String(checkedRadio?.value || optionValues[0].value || '');

            nextOptions.push({
              name: optionSyncName,
              syncName: optionSyncName,
              displayName: optionDisplayName,
              position: optionPosition,
              index: optionIndex,
              currentValue: currentValue,
              values: optionValues,
            });
          });
        }

        if (!nextOptions.length) return false;

        accordion.textContent = '';
        nextOptions.forEach((optionData) => {
          this.renderStickyAccordionOption(accordion, optionData);
        });
        return true;
      }
  
      setupVariantAccordion() {
        const accordion = this.querySelector('[data-sticky-variant-accordion]');
        if (!accordion) return;
  
        accordion.addEventListener('click', (e) => {
          const freeSockButton = e.target.closest('.sticky-tilo-free-sock-promo .tilo-free-sock-promo__size[data-sock-variant-id]');
          if (freeSockButton && !freeSockButton.hasAttribute('disabled')) {
            e.preventDefault();
            const sockVariantId = freeSockButton.getAttribute('data-sock-variant-id');
            this.syncFreeSockSelectionToMain(sockVariantId);
            const promoWrap = freeSockButton.closest('.sticky-tilo-free-sock-promo');
            if (promoWrap) {
              promoWrap.querySelectorAll('.tilo-free-sock-promo__size').forEach((btn) => btn.classList.remove('is-selected'));
            }
            freeSockButton.classList.add('is-selected');
            return;
          }

          const trigger = e.target.closest('.sticky-variant-accordion__trigger');
          const optionBtn = e.target.closest('.sticky-variant-accordion__option');
          if (trigger) {
            e.preventDefault();
            const item = trigger.closest('.sticky-variant-accordion__item');
            const panel = item && item.querySelector('.sticky-variant-accordion__panel');
            const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
            accordion.querySelectorAll('.sticky-variant-accordion__trigger').forEach(t => {
              t.setAttribute('aria-expanded', 'false');
            });
            accordion.querySelectorAll('.sticky-variant-accordion__panel').forEach(p => {
              p.setAttribute('hidden', '');
            });
            if (!isExpanded && panel) {
              trigger.setAttribute('aria-expanded', 'true');
              panel.removeAttribute('hidden');
            }
            return;
          }
          if (optionBtn && !optionBtn.disabled) {
            e.preventDefault();
            const optionName = optionBtn.dataset.optionName;
            const optionPosition = optionBtn.dataset.optionPosition;
            const optionIndex = optionBtn.dataset.optionIndex;
            const value = optionBtn.dataset.value;
            if (optionName != null && value != null) {
              this.syncVariantToMainForm(optionName, optionPosition, optionIndex, value);
              const item = optionBtn.closest('.sticky-variant-accordion__item');
              const trigger = item && item.querySelector('.sticky-variant-accordion__trigger');
              const currentValueEl = trigger && trigger.querySelector('[data-sticky-current-value]');
              if (currentValueEl) {
                currentValueEl.textContent = this.getDisplayValueForOption(optionName, value);
              }
              item.querySelectorAll('.sticky-variant-accordion__option').forEach(o => o.classList.remove('is-selected'));
              optionBtn.classList.add('is-selected');
              const panel = item.querySelector('.sticky-variant-accordion__panel');
              if (trigger) trigger.setAttribute('aria-expanded', 'false');
              if (panel) panel.setAttribute('hidden', '');
            }
          }
        });

        accordion.addEventListener('change', (e) => {
          const cloneControl = e.target.closest('[data-sticky-clone-control]');
          if (!cloneControl) return;

          const cloneIndex = cloneControl.getAttribute('data-clone-index');
          if (!cloneIndex) return;

          if (cloneControl instanceof HTMLInputElement) {
            if (cloneControl.type !== 'radio' || !cloneControl.checked) return;
            this.syncPromotionCloneSelectionToMain(cloneIndex, cloneControl.value);
            return;
          }

          if (cloneControl instanceof HTMLSelectElement) {
            this.syncPromotionCloneSelectionToMain(cloneIndex, cloneControl.value);
          }
        });
  
        document.addEventListener('click', (e) => {
          if (accordion.contains(e.target)) return;
          accordion.querySelectorAll('.sticky-variant-accordion__trigger').forEach(t => t.setAttribute('aria-expanded', 'false'));
          accordion.querySelectorAll('.sticky-variant-accordion__panel').forEach(p => p.setAttribute('hidden', ''));
        });
      }
  
      syncVariantToMainForm(optionName, optionPosition, optionIndex, value) {
        const variantSelector = document.querySelector('#variant-selector-' + this.sectionId);
        if (!variantSelector) return;
        const pickerType = variantSelector.dataset.pickerType || 'button';
        if (pickerType === 'dropdown' && optionIndex != null) {
          const select = variantSelector.querySelector('#Option-' + this.sectionId + '-' + optionIndex);
          if (select && value) {
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            this.syncTiloPromotionMultiSizeFromMain();
            setTimeout(() => this.updateStickyQuantityVisibility(), 0);
            setTimeout(() => this.syncTiloPromotionMultiSizeFromMain(), 120);
          }
        } else {
          const name = optionName + '-' + (optionPosition || '');
          const input = variantSelector.querySelector('input[type="radio"][name="' + name + '"][value="' + value + '"]');
          if (input) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            this.syncTiloPromotionMultiSizeFromMain();
            setTimeout(() => this.updateStickyQuantityVisibility(), 0);
            setTimeout(() => this.syncTiloPromotionMultiSizeFromMain(), 120);
          }
        }
      }
  
      setupClickHandler() {
        // Remove any existing listeners
        const newStickyButton = this.stickyButton.cloneNode(true);
        this.stickyButton.parentNode.replaceChild(newStickyButton, this.stickyButton);
        this.stickyButton = newStickyButton;
        
        this.stickyButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Remove 'open' class when add to cart button is clicked
          this.classList.remove('open');
          
          // Re-find button in case it was recreated
          const currentMainButton = document.getElementById(`AddToCart-${this.sectionId}`);
          if (currentMainButton) {
            // Check if button text indicates "Notify Me When Available"
            const mainAtcText = currentMainButton.querySelector('.atc-text');
            const buttonText = mainAtcText ? mainAtcText.textContent.trim() : currentMainButton.textContent.trim();
            
            if (buttonText.toLowerCase().includes('notify me')) {
              // Click the notify me popup trigger instead
              const scopedProductInfo = window.Shop?.productInfoEl || document.querySelector('product-info');
              const notifyMeTrigger = scopedProductInfo?.querySelector('.notify-me-popup-trigger');
              if (notifyMeTrigger) {
                notifyMeTrigger.click();
                return;
              }
            }
            
            // For regular "Add to cart" or other states, click the main button
            const stickyQuantityInput = this.querySelector('[data-sticky-quantity-input]');
            if (stickyQuantityInput) {
              this.syncQuantityToMainForm(stickyQuantityInput.value);
            }
            this.syncTiloPromotionMultiSizeFromMain();

            // Temporarily enable button if disabled to ensure click event fires
            const wasDisabled = currentMainButton.disabled;
            if (wasDisabled) {
              currentMainButton.disabled = false;
            }
            
            // Trigger click on main button - this will handle all logic including popups
            currentMainButton.click();
            
            // Restore disabled state if it was disabled
            if (wasDisabled) {
              // Use setTimeout to ensure click event completes first
              setTimeout(() => {
                currentMainButton.disabled = true;
              }, 0);
            }
          }
        });
      }
  
      updateVisibility() {
        // Show sticky only when form buttons, gallery, and variant selector are out of viewport
        if (this.formButtonsInViewport || this.galleryInViewport || this.variantSelectorInViewport) {
          this.classList.remove('is-visible');
        } else {
          this.classList.add('is-visible');
        }
      }

      setupFormButtonsVisibility() {
        const formButtons = document.querySelector('.product-form__buttons');
        if (!formButtons || typeof IntersectionObserver === 'undefined') {
          // If no form buttons found, show sticky by default
          this.formButtonsInViewport = false;
          this.updateVisibility();
          return;
        }
        
        const self = this;
        
        // Check initial state before setting up observer
        const checkIfInViewport = () => {
          const rect = formButtons.getBoundingClientRect();
          // Element is "in viewport" if:
          // - Its top is above the viewport bottom
          // - Its bottom is below the top of the viewport
          return rect.top < window.innerHeight && rect.bottom > 0;
        };
        
        // Set initial state
        this.formButtonsInViewport = checkIfInViewport();
        this.updateVisibility();
        
        // Create observer with callback that handles visibility
        const observerCallback = function (entries) {
          entries.forEach(function (entry) {
            // entry.isIntersecting will be true when element is in viewport, false when it leaves
            self.formButtonsInViewport = entry.isIntersecting;
            self.updateVisibility();
          });
        };
        
        this.formButtonsObserver = new IntersectionObserver(observerCallback, { 
          root: null, 
          rootMargin: '0px', 
          threshold: 0 
        });
        
        this.formButtonsObserver.observe(formButtons);
        
        // Double-check initial state after a short delay to catch any timing issues
        setTimeout(() => {
          const currentState = checkIfInViewport();
          if (self.formButtonsInViewport !== currentState) {
            self.formButtonsInViewport = currentState;
            self.updateVisibility();
          }
        }, 0);
      }

      setupGalleryVisibility() {
        const galleryEl = document.querySelector('.gallery-slider');
        if (!galleryEl || typeof IntersectionObserver === 'undefined') {
          // If no gallery is found, do not block sticky visibility.
          this.galleryInViewport = false;
          this.updateVisibility();
          return;
        }

        const checkIfInViewport = () => {
          const rect = galleryEl.getBoundingClientRect();
          return rect.top < window.innerHeight && rect.bottom > 0;
        };

        this.galleryInViewport = checkIfInViewport();
        this.updateVisibility();

        this.galleryObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.galleryInViewport = entry.isIntersecting;
            this.updateVisibility();
          });
        }, {
          root: null,
          rootMargin: '0px',
          threshold: 0
        });

        this.galleryObserver.observe(galleryEl);

        setTimeout(() => {
          const currentState = checkIfInViewport();
          if (this.galleryInViewport !== currentState) {
            this.galleryInViewport = currentState;
            this.updateVisibility();
          }
        }, 0);
      }

      setupVariantSelectorVisibility() {
        const variantSelectorEl = document.querySelector('.variant-select__button');
        if (!variantSelectorEl || typeof IntersectionObserver === 'undefined') {
          // If no selector is found, do not block sticky visibility.
          this.variantSelectorInViewport = false;
          this.updateVisibility();
          return;
        }

        const checkIfInViewport = () => {
          const rect = variantSelectorEl.getBoundingClientRect();
          return rect.top < window.innerHeight && rect.bottom > 0;
        };

        this.variantSelectorInViewport = checkIfInViewport();
        this.updateVisibility();

        this.variantSelectorObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.variantSelectorInViewport = entry.isIntersecting;
            this.updateVisibility();
          });
        }, {
          root: null,
          rootMargin: '0px',
          threshold: 0
        });

        this.variantSelectorObserver.observe(variantSelectorEl);

        setTimeout(() => {
          const currentState = checkIfInViewport();
          if (this.variantSelectorInViewport !== currentState) {
            this.variantSelectorInViewport = currentState;
            this.updateVisibility();
          }
        }, 0);
      }

      setupMobileWrapperClick() {
        const mobileWrapper = this.querySelector('.select-product-mobile-wrapper');
        if (!mobileWrapper) return;
        
        this.mobileWrapperClickHandler = () => {
          this.classList.add('open');
        };
        
        mobileWrapper.addEventListener('click', this.mobileWrapperClickHandler);
      }

      setupMobileSelectCrossClick() {
        const mobileSelectCross = this.querySelector('.mobile-select-cross span');
        if (!mobileSelectCross) return;
        
        this.mobileSelectCrossClickHandler = () => {
          this.classList.remove('open');
        };
        
        mobileSelectCross.addEventListener('click', this.mobileSelectCrossClickHandler);
      }

      setupMobileTabsClick() {
        // Use event delegation on the tabs container for better reliability
        const tabsContainer = this.querySelector('.sticky-mobile-tabs');
        if (!tabsContainer) return;
        
        // Use a single handler with event delegation
        this.mobileTabsClickHandler = (e) => {
          const clickedTab = e.target.closest('.sizes-tab, .size-guide-tab');
          if (!clickedTab) return;
          
          const sizesTab = tabsContainer.querySelector('.sizes-tab');
          const sizeGuideTab = tabsContainer.querySelector('.size-guide-tab');
          const sizesContent = this.querySelector('.sticky-variant-accordion.sizes-tab');
          const sizeGuideContent = this.querySelector('.sticky-mobile-size-guide.size-guide-tab');
          
          if (clickedTab.classList.contains('sizes-tab')) {
            // Show sizes tab
            if (sizesTab) sizesTab.classList.add('active');
            if (sizeGuideTab) sizeGuideTab.classList.remove('active');
            if (sizesContent) {
              sizesContent.style.display = '';
              sizesContent.style.visibility = 'visible';
            }
            if (sizeGuideContent) {
              sizeGuideContent.style.display = 'none';
              sizeGuideContent.style.visibility = 'hidden';
            }
          } else if (clickedTab.classList.contains('size-guide-tab')) {
            // Show size guide tab
            if (sizeGuideTab) sizeGuideTab.classList.add('active');
            if (sizesTab) sizesTab.classList.remove('active');
            if (sizeGuideContent) {
              sizeGuideContent.style.display = '';
              sizeGuideContent.style.visibility = 'visible';
            }
            if (sizesContent) {
              sizesContent.style.display = 'none';
              sizesContent.style.visibility = 'hidden';
            }
          }
        };
        
        tabsContainer.addEventListener('click', this.mobileTabsClickHandler);
      }

      setupScrollHandler() { 
        this.handleScroll = () => {
          this.updateVisibility();
        };

        window.addEventListener('scroll', this.handleScroll, { passive: true });
        this.handleScroll(); // Initial check
      }
  
      setupStateSync() {
        // Watch for changes to main button
        this.observer = new MutationObserver(() => {
          this.syncButtonState();
        });
  
        if (this.mainButton) {
          this.observer.observe(this.mainButton, { 
            attributes: true, 
            attributeFilter: ['class'],
            childList: true,
            subtree: true,
            characterData: true
          });
        }
      }
  
      syncButtonState() {
        // Re-find main button in case it was recreated
        const currentMainButton = document.getElementById(`AddToCart-${this.sectionId}`);
        if (!currentMainButton || !this.stickyButton) {
          // Update reference if button was recreated
          if (currentMainButton && currentMainButton !== this.mainButton) {
            this.mainButton = currentMainButton;
            // Re-setup observer for new button
            if (this.observer) {
              this.observer.disconnect();
            }
            this.observer = new MutationObserver(() => {
              this.syncButtonState();
            });
            this.observer.observe(this.mainButton, { 
              attributes: true, 
              attributeFilter: ['class'],
              childList: true,
              subtree: true,
              characterData: true
            });
          }
          return;
        }
        
        // Sticky button should never be disabled - always allow clicks
        // This ensures "Notify Me When Available" popup works
        this.stickyButton.disabled = false;
        
        // Sync button text - check both .atc-text and direct textContent
        const mainAtcText = currentMainButton.querySelector('.atc-text');
        const stickyAtcText = this.stickyButton.querySelector('.atc-text');
        if (mainAtcText && stickyAtcText) {
          const mainText = mainAtcText.textContent.trim();
          const stickyText = stickyAtcText.textContent.trim();
          if (mainText !== stickyText) {
            stickyAtcText.textContent = mainText;
          }
        } else if (!mainAtcText && !stickyAtcText) {
          // Fallback: sync direct textContent if no .atc-text elements
          const mainText = currentMainButton.textContent.trim();
          const stickyText = this.stickyButton.textContent.trim();
          if (mainText !== stickyText && !mainText.includes('loading')) {
            this.stickyButton.textContent = mainText;
          }
        }

        // Sync price into sticky "Add to cart - $X.XX"
        const stickyPriceEl = this.stickyButton.querySelector('.sticky-atc-price');
        if (stickyPriceEl) {
          // Find the original price from .price-item span (not variant-specific)
          const priceContainer = document.querySelector('.product__price, #price-' + this.sectionId);
          const priceEl = priceContainer && priceContainer.querySelector('.price-item');
          const mainPriceText = priceEl ? priceEl.textContent.trim() : null;
          if (mainPriceText) {
            const currentPrice = stickyPriceEl.textContent.replace(/^\s*-\s*/, '').trim();
            if (currentPrice !== mainPriceText) {
              stickyPriceEl.textContent = ' - ' + mainPriceText;
            }
          }
        }
        
        // Sync loader state
        const mainLoader = currentMainButton.querySelector('.loading__spinner');
        const stickyLoader = this.stickyButton.querySelector('.loading__spinner');
        if (mainLoader && stickyLoader) {
          const mainLoaderHidden = mainLoader.classList.contains('hidden');
          const stickyLoaderHidden = stickyLoader.classList.contains('hidden');
          if (mainLoaderHidden !== stickyLoaderHidden) {
            stickyLoader.classList.toggle('hidden', mainLoaderHidden);
          }
        }
        
        // Sync button styles
        const mainButtonStyles = window.getComputedStyle(currentMainButton);
        this.stickyButton.style.color = mainButtonStyles.color;
        this.stickyButton.style.backgroundColor = mainButtonStyles.backgroundColor;

        const stickyQuantityInput = this.querySelector('[data-sticky-quantity-input]');
        const mainQuantityInput = this.getMainQuantityInput();
        if (stickyQuantityInput && mainQuantityInput) {
          const mainMin = mainQuantityInput.getAttribute('min');
          const mainMax = mainQuantityInput.getAttribute('max');
          const mainStep = mainQuantityInput.getAttribute('step');

          if (mainMin != null) stickyQuantityInput.setAttribute('min', mainMin);
          if (mainMax != null) stickyQuantityInput.setAttribute('max', mainMax);
          else stickyQuantityInput.removeAttribute('max');
          if (mainStep != null) stickyQuantityInput.setAttribute('step', mainStep);

          if (document.activeElement !== stickyQuantityInput && stickyQuantityInput.value !== mainQuantityInput.value) {
            stickyQuantityInput.value = mainQuantityInput.value;
          }
        }

        this.updateStickyQuantityVisibility();
      }

      /**
       * TILO PDP: keep sticky bar in sync when color-variants / style-variants swap product JSON.
       */
      syncFromProductData(productData, variant) {
        if (!productData || typeof productData !== 'object') return;

        if (productData.url) {
          this.dataset.productUrl = productData.url;
        }

        const titleEl = this.querySelector('.product-info-sticky__title');
        if (titleEl && productData.title) {
          titleEl.textContent = productData.title;
        }

        const imgLink = this.querySelector('.product-info-sticky__image');
        if (imgLink && productData.url) {
          imgLink.setAttribute('href', productData.url);
        }

        const img = this.querySelector('.product-info-sticky__image img');
        if (img && productData.media && productData.media.length) {
          const m = productData.media[0];
          let src = m.src;
          if (m.media_type && m.media_type !== 'image') {
            src = (m.preview_image && m.preview_image.src) || m.src;
          }
          if (src) {
            try {
              const u = new URL(src, window.location.origin);
              u.searchParams.set('width', '150');
              img.src = u.toString();
            } catch (e) {
              img.src = src;
            }
          }
          img.alt = productData.title || '';
        }

        const accordion = this.querySelector('[data-sticky-variant-accordion]');
        if (accordion) {
          if (productData.has_only_default_variant) {
            accordion.innerHTML = '';
          } else {
            const rebuilt = this.rebuildStickyAccordionFromVariantSelector();
            if (!rebuilt) {
              setTimeout(() => {
                this.rebuildStickyAccordionFromVariantSelector();
              }, 120);
            }
            setTimeout(() => {
              this.rebuildStickyAccordionFromVariantSelector();
            }, 350);
          }
          this.syncStickyAccordionFromVariantSelector(productData);
          this.syncStickyPromotionClonePanels();
          this.syncStickyFreeSockPromoPanel();
        }

        this.syncButtonState();
      }

      syncStickyAccordionFromVariantSelector(productData) {
        if (!productData || !productData.options) return;
        const vs = document.querySelector('#variant-selector-' + this.sectionId);
        const accordion = this.querySelector('[data-sticky-variant-accordion]');
        if (!vs || !accordion) return;

        productData.options.forEach((option, optionIndex) => {
          if ((option.name || '').toLowerCase() === 'color') return;

          let selectedValue = null;
          const select = vs.querySelector('#Option-' + this.sectionId + '-' + optionIndex);
          if (select) {
            selectedValue = select.value;
          } else {
            const nameTarget = option.name + '-' + option.position;
            vs.querySelectorAll('input[type="radio"]:checked').forEach(function (r) {
              if (r.name === nameTarget) selectedValue = r.value;
            });
          }

          const item = accordion.querySelector(
            '.sticky-variant-accordion__item[data-option-position="' + option.position + '"]'
          );
          if (!item) return;

          const triggerVal = item.querySelector('[data-sticky-current-value]');
          if (triggerVal && selectedValue != null) {
            triggerVal.textContent = this.getDisplayValueForOption(option.name, selectedValue);
          }

          item.querySelectorAll('.sticky-variant-accordion__option').forEach((o) => {
            o.classList.toggle('is-selected', o.dataset.value === selectedValue);
          });
        });
      }
    }

    customElements.define('sticky-add-to-cart', StickyAddToCart);
}

/**
 * Called from component-color-variants.js & component-style-variants.js when swapping products.
 */
window.WarRoadSyncStickyAtcTilo = function (productData, variant) {
  var stickyElements = document.querySelectorAll('sticky-add-to-cart');
  if (!stickyElements.length) return;
  stickyElements.forEach(function (el) {
    if (typeof el.syncFromProductData === 'function') {
      el.syncFromProductData(productData, variant);
    }
  });
};

window.WarRoadScheduleStickyAtcTiloSync = function (productData) {
  if (typeof window.WarRoadSyncStickyAtcTilo !== 'function' || !productData) return;
  if (!document.querySelector('sticky-add-to-cart')) return;

  function tick() {
    var form = document.querySelector('product-info form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    var input = form && form.querySelector('input[name="id"]');
    var vid = input && input.value;
    var v = null;
    if (vid && productData.variants) {
      v = productData.variants.find(function (x) {
        return String(x.id) === String(vid);
      });
    }
    if (!v && productData.variants && productData.variants[0]) {
      v = productData.variants[0];
    }
    window.WarRoadSyncStickyAtcTilo(productData, v);
  }

  tick();
  setTimeout(tick, 400);
  setTimeout(tick, 900);
  setTimeout(tick, 1400);
};
